import {
    Area,
    AreaChart,
    Rectangle,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { CategoricalChartState } from 'recharts/types/chart/generateCategoricalChart';
import { AxisDomain } from 'recharts/types/util/types';

type ChartProps = {
    data: Array<{ time: string; value: number }>;
    selectedTimeframe?: string;
    domain?: AxisDomain;
    onActivePayloadChange?: (newPayload: number | undefined) => void;
    tickFormatter?: (value: any) => string;
};

const Chart: React.FC<ChartProps> = ({
    data,
    selectedTimeframe,
    domain,
    onActivePayloadChange,
    tickFormatter,
}) => {
    const defaultTickFormatter = (value: any) => {
        if (selectedTimeframe === '1y') {
            return new Intl.DateTimeFormat('en-US', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            }).format(new Date(value));
        } else if (selectedTimeframe === '1m' || selectedTimeframe === '1w') {
            return new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            }).format(new Date(value));
        } else {
            return new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: 'numeric',
            }).format(new Date(value));
        }
    };

    const handlePayloadChange = (chartState: CategoricalChartState | undefined) => {
        onActivePayloadChange &&
            onActivePayloadChange(chartState?.activePayload?.[0]?.payload?.value);
    };

    return (
        <div className='chart'>
            <ResponsiveContainer width='100%' height='100%'>
                <AreaChart
                    data={data}
                    onMouseMove={handlePayloadChange}
                    onMouseLeave={() => handlePayloadChange(undefined)}
                    margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                >
                    <defs>
                        <linearGradient id='areaGradient' x1='1' y1='0' x2='0' y2='0'>
                            <stop stopColor='#2dcabb' stopOpacity='.5' />
                            <stop offset='.884' stopColor='#0ff' stopOpacity='0' />
                        </linearGradient>
                        <linearGradient id='cursorGradient' x1='.5' x2='.5' y2='1'>
                            <stop stopColor='#00165b' stopOpacity='0' />
                            <stop offset='1' stopColor='#0ff' stopOpacity='.62' />
                        </linearGradient>
                        <linearGradient id='dotGradient'>
                            <stop stopColor='#00A6FF' />
                            <stop offset='1' stopColor='#00FFFF' />
                        </linearGradient>
                    </defs>

                    <line width='100%' height='1px' y='100%' />
                    <XAxis
                        dataKey='time'
                        tick={{ fill: 'rgba(255,255,255,0.5)' }}
                        tickLine={false}
                        axisLine={<line stroke='rgba(255,255,255,0.5)' />}
                        interval={'preserveEnd'}
                        minTickGap={30}
                        tickFormatter={tickFormatter ?? defaultTickFormatter}
                    />
                    <YAxis dataKey='value' hide domain={domain} />
                    <Tooltip content={() => <></>} cursor={<CustomCursor />} />
                    <Area
                        type='monotone'
                        dataKey='value'
                        stroke='#00FFFF'
                        strokeWidth={2}
                        fill='url(#areaGradient)'
                        activeDot={<CustomDot />}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default Chart;

const CURSOR_WIDTH = 62;
const CustomCursor = (props: any) => (
    <Rectangle
        {...props}
        x={props.points[0].x - CURSOR_WIDTH / 2}
        width={62}
        height={props.height + 30}
        fill='url(#cursorGradient)'
        stroke='transparent'
    />
);

const CustomDot = (props: any) => {
    // removing dataKey since we don't need it and it was causing warnings
    const { dataKey, ...rest } = props;

    return (
        <circle
            {...rest}
            r={12}
            stroke='white'
            strokeWidth={2.6}
            fill='url(#dotGradient)'
        />
    );
};
